import { lazy, Suspense, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { PanelBottom } from 'lucide-react'
import { useAppStore } from '@core/store'
import {
  useDetachedWindow,
  type DetachedWindowHandlers,
} from '@core/layout/janela-destacada/useDetachedWindow'

const PdfReviewWorkspace = lazy(() => import('./PdfReviewWorkspace'))

// A janela precisa de nome PRÓPRIO. Dois destaques com o mesmo nome
// reaproveitam a janela do browser, e destacar aqui deitava abaixo o
// desenho 2D ou os conflitos de quem já os tivesse destacados.
const PDF_WINDOW_NAME = 'vn2r-pdf-review'
const PDF_WINDOW_TITLE = 'VN2R openBIM Hub · Revisão PDF'
// Mais larga do que alta: um desenho abre-se em paisagem, e a barra de
// ferramentas do motor é comprida.
const PDF_WINDOW_FEATURES =
  'width=1440,height=960,resizable=yes,scrollbars=yes'

const CARREGAR = (
  <div className="flex h-full items-center justify-center bg-[var(--color-bg)] text-sm text-[var(--color-text-muted)]">
    A carregar revisão PDF…
  </div>
)

/** Full-canvas host used in local mode, where optional modules have no route. */
export default function PdfReviewHost() {
  const open = useAppStore((state) => state.pdfReviewOpen)
  const setOpen = useAppStore((state) => state.setPdfReviewOpen)
  const detached = useAppStore((state) => state.pdfReviewDetached)
  const setDetached = useAppStore((state) => state.setPdfReviewDetached)
  const pushToast = useAppStore((state) => state.pushToast)

  const handlers = useMemo<DetachedWindowHandlers>(
    () => ({
      onBlocked: () => {
        setDetached(false)
        pushToast(
          'O browser bloqueou a janela. Permite janelas para este site e tenta outra vez.',
          { type: 'error' },
        )
      },
      onClose: () => setDetached(false),
    }),
    [setDetached, pushToast],
  )

  const { container } = useDetachedWindow(open && detached, handlers, {
    name: PDF_WINDOW_NAME,
    title: PDF_WINDOW_TITLE,
    features: PDF_WINDOW_FEATURES,
  })

  if (!open) return null

  const workspace = (
    <Suspense fallback={CARREGAR}>
      <PdfReviewWorkspace
        onClose={() => {
          setDetached(false)
          setOpen(false)
        }}
        detached={detached}
        onToggleDetach={() => setDetached(!detached)}
      />
    </Suspense>
  )

  if (detached) {
    if (!container) return null
    return createPortal(
      <>
        <div className="flex h-8 shrink-0 items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-panel)] px-2 text-xs">
          <span className="truncate text-[var(--color-text-muted)]">
            VN2R openBIM Hub · Revisão PDF (janela destacada)
          </span>
          <button
            type="button"
            onClick={() => setDetached(false)}
            title="Voltar a mostrar a revisão por cima do 3D"
            className="flex items-center gap-1 rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            <PanelBottom size={11} />
            Voltar ao painel
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[var(--color-bg)] text-[var(--color-text)]">
          {workspace}
        </div>
      </>,
      container,
    )
  }

  return <div className="absolute inset-0 z-[70]">{workspace}</div>
}
